import {pluralize} from 'BenchlingUtil';
import {AttachPrimerOptions} from 'constants/AttachPrimerConstants';
import {SequenceTopology} from 'constants/SequenceConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import FileStates, {SequenceFileState} from 'FileStates';
import * as SequenceDAO from 'molbio/dao/SequenceDAO';
import notifications, {Response} from 'notifications';
import * as TrayTaskActions from 'tray/TrayTaskActions';
import * as TrayTaskConstants from 'tray/TrayTaskConstants';
import * as TaskRunner from 'util/TaskRunner';
import {OngoingTaskResponse} from 'util/TaskRunner';

export function batchAutoAnnotate(sequenceIds: Array<string>, featureLibraryIds: Array<number>): void {
  SequenceDAO.batchAutoAnnotate(sequenceIds, featureLibraryIds)
    .then((response: OngoingTaskResponse) => {
      const taskId = TrayTaskActions.createTrayTask({
        heading: `Auto-annotating ${sequenceIds.length} DNA sequences`,
      });
      return (
        TaskRunner.resumeTask(response.status_url)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .progress((progress: any) => {
            TrayTaskActions.updateTrayTask(taskId, {progress});
          })
          .then(() => FileStates.get().updateSequenceVersions(sequenceIds, {}))
          .done(() => {
            TrayTaskActions.updateTrayTask(taskId, {
              progress: TrayTaskConstants.SUCCESS_PROGRESS,
              heading: `Annotated ${sequenceIds.length} DNA sequences`,
            });
          })
          .fail(() => {
            const error = 'An error occurred while annotating the DNA sequences. Please try again.';
            TrayTaskActions.updateTrayTask(taskId, {progress: TrayTaskConstants.FAILURE_PROGRESS, error});
          })
      );
    })
    .fail((res: Response) => {
      notifications.notifyError(res);
    });
}

export async function bulkRemoveAnnotations(sequenceIds: Array<string>): Promise<void> {
  await SequenceDAO.bulkRemoveAnnotations(sequenceIds);
  notifications.success(
    `Removed all annotations from ${sequenceIds.length} ${pluralize(sequenceIds.length, 'sequence')}`
  );
}

export async function bulkRemoveTranslations(sequenceIds: Array<string>): Promise<void> {
  await SequenceDAO.bulkRemoveTranslations(sequenceIds);
  notifications.success(
    `Removed all translations from ${sequenceIds.length} ${pluralize(sequenceIds.length, 'sequence')}`
  );
}

export async function bulkAttachPrimers(
  sequenceIds: Array<string>,
  attachPrimersOptions: AttachPrimerOptions
): Promise<void> {
  // Create the task before kicking off the first request so that the user
  // sees immediate feedback from their action
  const taskId = TrayTaskActions.createTrayTask({
    heading: `Adding primers to ${sequenceIds.length} DNA ${pluralize(sequenceIds.length, 'sequence')}`,
    progress: TrayTaskConstants.INDETERMINATE_PROGRESS,
  });

  try {
    await SequenceDAO.bulkAttachPrimers(sequenceIds, attachPrimersOptions);
    TrayTaskActions.updateTrayTask(taskId, {
      progress: TrayTaskConstants.SUCCESS_PROGRESS,
      heading: `Added primers to ${sequenceIds.length} DNA ${pluralize(sequenceIds.length, 'sequence')}`,
    });
  } catch (e) {
    if (!e.userMessage) {
      TrayTaskActions.removeTrayTask(taskId);
      throw e;
    }

    // from BadRequestError
    TrayTaskActions.updateTrayTask(taskId, {
      progress: TrayTaskConstants.FAILURE_PROGRESS,
      error: e.userMessage,
    });
  }
}

export async function bulkDetachPrimers(sequenceIds: Array<string>): Promise<void> {
  await SequenceDAO.bulkDetachPrimers(sequenceIds);
  notifications.success(
    `Detached all primers from ${sequenceIds.length} ${pluralize(sequenceIds.length, 'sequence')}`
  );
}

export async function bulkUpdateSequenceTopology(
  sequenceIds: Array<string>,
  topology: SequenceTopology
): Promise<void> {
  await SequenceDAO.bulkUpdateSequences(sequenceIds, {circular: topology === SequenceTopology.CIRCULAR});
  notifications.success(
    `Updated topology of ${sequenceIds.length} ${pluralize(sequenceIds.length, 'sequence')} to ${topology}`
  );
  FileStates.get().updateSequenceVersions(sequenceIds, {});
}

// Opens a sequence to the given version if it still exists.
export function openSequenceVersion(sequenceId: string, versionId: number): void {
  FileBrowserActions.openFile(sequenceId, {
    onOpen(fileState: SequenceFileState) {
      const versions = fileState.get('sequence').get('versions');
      const version = versions.get(versionId);
      if (version != null) {
        versions.trigger('open', version);
      } else {
        notifications.warn('This version no longer exists.');
      }
    },
  });
}
